/* eslint-disable perfectionist/sort-objects */
import { isTravellingWithoutAdult, NO_ADULT_MESSAGE } from '@massilia/contracts'
import { getMaxSearchDate } from '@massilia/features'
import { format } from 'date-fns'
import { z } from 'zod'

export const DEFAULT_OPTION: FlightFormOption = {
  label: '',
  value: '',
  category: 'airport',
}

export const DEFAULT_FLIGHT_LET: FlightSearchSchemaType['flight'][number] = {
  date: {},
  departure: DEFAULT_OPTION,
  destination: DEFAULT_OPTION,
}

export const DEFAULT_VALUES: FlightSearchSchemaType = {
  flight: [DEFAULT_FLIGHT_LET],
  passengers: {
    adults: 1,
    babies: 0,
    infants: 0,
    seniors: 0,
    youngsters: 0,
  },
  travelClass: 'ECONOMY',
  type: 'round-trip',
}

export type FlightFormOption = z.infer<typeof optionSchema>

export const passengerCategories = ['adults', 'babies', 'infants', 'seniors', 'youngsters'] as const

export type PassengerCategory = (typeof passengerCategories)[number]

export interface PassengersClass {
  descriptionLabel: string
  titleLabel: string
}

export const passengerCategoriesLabels = {
  adults: {
    descriptionLabel: 'De 25 à 59 ans',
    titleLabel: 'Adultes',
  },
  babies: {
    descriptionLabel: 'Moins de 2 ans',
    titleLabel: 'Bébés',
  },
  infants: {
    descriptionLabel: 'De 2 à 11 ans',
    titleLabel: 'Enfants',
  },
  seniors: {
    descriptionLabel: 'Plus de 60 ans',
    titleLabel: 'Seniors',
  },
  youngsters: {
    descriptionLabel: 'De 12 à 25 ans',
    titleLabel: 'Jeunes',
  },
} satisfies Record<PassengerCategory, PassengersClass>

const passengerSchema = z
  .object({
    adults: z.number().min(0),
    babies: z.number().min(0),
    infants: z.number().min(0),
    seniors: z.number().min(0),
    youngsters: z.number().min(0),
  })
  .superRefine((data, ctx) => {
    const totalPassengers = Object.values(data).reduce((total, value) => total + value, 0)

    if (totalPassengers === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Au moins un passager est requis',
        path: [],
      })
    }

    const totalWithoutBabies = data.adults + data.youngsters + data.infants + data.seniors
    if (totalWithoutBabies > 9) {
      ctx.addIssue({
        code: 'custom',
        message: 'Maximum 9 passagers (hors bébés)',
        path: [],
      })
    }

    if (data.babies > 9) {
      ctx.addIssue({
        code: 'custom',
        message: 'Maximum 9 bébés',
        path: [],
      })
    }

    const accompanyingAdults = data.adults + data.youngsters + data.seniors
    if (data.babies > accompanyingAdults) {
      ctx.addIssue({
        code: 'custom',
        message: "Il doit y avoir au moins autant d'adultes/jeunes/seniors que de bébés",
        path: [],
      })
    }

    // Règle et message partagés avec le chatbot, qui remplit la même
    // composition par un autre chemin — voir `minors-travelling-alone.ts`.
    if (isTravellingWithoutAdult(data)) {
      ctx.addIssue({
        code: 'custom',
        message: NO_ADULT_MESSAGE,
        path: [],
      })
    }
  })

const travelClassTypes = {
  economy: 'ECONOMY',
  premium: 'PREMIUM',
  business: 'BUSINESS',
} as const

export type TravelClassType = (typeof travelClassTypes)[keyof typeof travelClassTypes]

const travelClassSchema = z.enum(Object.values(travelClassTypes) as [TravelClassType])

export const travelClassTypesLabels = {
  ECONOMY: 'Éco',
  PREMIUM: 'Premium',
  BUSINESS: 'Business',
} satisfies Record<TravelClassType, string>

const optionSchema = z
  .object({
    label: z.string().min(1),
    value: z.string().min(1),
    category: z.enum(['airport', 'city']).default('airport'),
  })
  .refine(
    (data) => data.label.length > 0 && data.value.length > 0,
    'Ajouter un aéroport ou une ville'
  )

const flightLegSchema = z.object({
  date: z.object({
    from: z.date().optional(),
    to: z.date().optional(),
  }),
  departure: optionSchema,
  destination: optionSchema,
})

const baseNewFlightSchema = z.object({
  passengers: passengerSchema,
  travelClass: travelClassSchema,
  type: z.enum(['one-way', 'round-trip', 'multi-destination']),
  /**
   * Forçage du bagage en soute. Plus aucun interrupteur dans la barre de recherche :
   * `POST /research_bar/search` lance les deux axes (avec et sans forçage) et en
   * réunit les offres, le filtre « bagage » de la page résultats se contentant de
   * filtrer la liste. Seul le formulaire multi-destinations du chatbot le pose
   * encore, son chemin (`/chat/flight/search-multi`) n'étant pas multiplexé.
   */
  withLuggage: z.boolean().optional(),
})

export const flightSearchSchema = z
  .discriminatedUnion('type', [
    baseNewFlightSchema.extend({
      flight: z.array(flightLegSchema).length(1),
      type: z.literal('one-way'),
    }),
    baseNewFlightSchema.extend({
      flight: z.array(flightLegSchema).length(1),
      type: z.literal('round-trip'),
    }),
    baseNewFlightSchema.extend({
      flight: z.array(flightLegSchema).min(2),
      type: z.literal('multi-destination'),
    }),
  ])
  .superRefine((data, ctx) => {
    // SEARCH-HORIZON — aucune date au-delà de J+358, tous types de trajet confondus.
    // Garde-fou pour les valeurs pré-remplies depuis l'URL (`/vols/resultats?flightSearch=...`),
    // qui ne passent pas par le calendrier.
    const maxSearchDate = getMaxSearchDate()
    for (const [i, leg] of data.flight.entries()) {
      for (const key of ['from', 'to'] as const) {
        const date = leg.date[key]
        if (date && date > maxSearchDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Nous ne proposons pas de vols au-delà du ${format(maxSearchDate, 'dd/MM/yyyy')}`,
            path: ['flight', i, 'date', key],
          })
        }
      }
    }

    if (data.type === 'round-trip') {
      if (data.flight[0].date.from === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Date de départ invalide: la date de départ doit être définie',
          path: ['flight', 0, 'date'],
        })
      }
      if (data.flight[0].date.to === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Date de retour invalide: la date de retour doit être définie',
          path: ['flight', 0, 'date'],
        })
      }
    }

    if (data.type !== 'multi-destination') return

    for (let i = 0; i < data.flight.length; i++) {
      if (i === 0) continue
      if (data.flight[i]?.date.from === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Date de départ invalide: la date de départ doit être définie',
          path: ['flight', i, 'date', 'from'],
        })
        continue
      }

      if (data.flight[i - 1]?.date.from === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Date de départ invalide: la date de départ doit être définie',
          path: ['flight', i - 1, 'date', 'to'],
        })
        continue
      }

      //NOTE: we can safely cast becasue we've already checked for undefined
      const fromCurrent = data.flight[i]?.date.from as Date
      const fromPrevious = data.flight[i - 1]?.date.from as Date

      if (fromCurrent < fromPrevious) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Date invalide: la date doit être postérieure à celle du segment précédent',
          path: ['flight', i, 'date', 'from'],
        })
      }
    }
  })

export type FlightLegType = z.infer<typeof flightLegSchema>
export type FlightSearchSchemaType = z.infer<typeof flightSearchSchema>
